import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {
  ResponsiveContainer,
  BarChart,
  Legend,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  Tooltip,
} from 'recharts';
import {Icon, Table, Loader} from 'semantic-ui-react';
import _ from 'lodash';
import {barColors, labels} from '../../../../../common/dictionaries/analysis';
import {colors} from '../../../../../common/styles/site';
import './weeklyChart.scss';

class SalesAndOrdersChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      'activeIndex': 0,
      'type': '',
    };
    this.handleClick = this.handleClick.bind(this);
    this.renderTooltip = this.renderTooltip.bind(this);
  }

  getBarType(barColor) {
    if (barColor === barColors.sales.base) {
      return 'sales';
    } else if (barColor === barColors.orders.base) {
      return 'orders';
    }
    return 'lostSales';
  }

  handleClick(data, index) {
    const type = this.getBarType(data.fill);
    this.setState({'activeIndex': index, type});

    if (type !== 'lostSales') {
      this.props.activeBarClick(type, data.week);
    }
  }

  renderTotals(moveType) {
    const {data} = this.props;
    if (!data || !data.length) {
      return null;
    }
    return ` (${data.reduce((sum, element) => sum + element[moveType], 0)})`;
  }

  renderLegend(props) {
    const {payload} = props;

    return (
      <ul className='recharts-default-legend'>
        {payload.map((entry, index) => (
          <div
            className={`recharts-legend-item legend-item-${index}`}
            key={`item-${index}`}
          >
            <Icon
              name='square'
              size='large'
              className={
                entry.value === 'lostSales' ? 'lost-sales' : entry.value
              }
            />
            {labels[entry.value]}
            {this.renderTotals(entry.value)}
          </div>
        ))}
      </ul>
    );
  }

  renderXLabels(val, axis) {
    return `S${val}`;
  }

  renderTooltip({active, payload}) {
    if (!active) {
      return null;
    }

    const {lostSales, orders} = this.props;

    return (
      <div className='weekly-data-tooltip'>
        {`Ventas: ${payload[0].payload.sales}`}
        {orders ? ` - Pedidos: ${payload[0].payload.orders}` : null}
        {lostSales ?
          ` - Ventas Perdidas: ${payload[0].payload.lostSales}` :
          null}
      </div>
    );
  }

  renderWeeklyData(list) {
    if (!list || !list.length) {
      return (
        <Table.Row>
          <Table.Cell textAlign='center' colSpan={13}>
            No hay datos disponibles
          </Table.Cell>
        </Table.Row>
      );
    }
    return list.map((element, index) => {
      return (
        <Table.Row key={index}>
          <Table.Cell textAlign='center'>{element.quantity}</Table.Cell>
          <Table.Cell textAlign='center'>{element.dealer}</Table.Cell>
          <Table.Cell textAlign='center'>{element.agency}</Table.Cell>
          <Table.Cell textAlign='center'>{element.user}</Table.Cell>
          <Table.Cell textAlign='center'>
            {this.state.type === 'sales' ? element.invoice : element.orderCode}
          </Table.Cell>
          {this.state.type === 'sales' && (
            <Table.Cell textAlign='center'>{element.documentType}</Table.Cell>
          )}
        </Table.Row>
      );
    });
  }

  renderTable() {
    const {loadingWeeklyAnalysis, weeklyAnalysis} = this.props;

    if (this.state.type === '' || this.state.type === 'lostSales') {
      return null;
    }

    return (
      <Table
        striped
        className={this.state.type === 'sales' ? 'sales-data' : 'orders-data'}
      >
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell textAlign='center'>
              Cantidad ({_.sumBy(weeklyAnalysis, 'quantity')})
            </Table.HeaderCell>
            <Table.HeaderCell textAlign='center'>
              Concesionario
            </Table.HeaderCell>
            <Table.HeaderCell textAlign='center'>Agencia</Table.HeaderCell>
            <Table.HeaderCell textAlign='center'>Usuario</Table.HeaderCell>
            <Table.HeaderCell textAlign='center'>
              {this.state.type === 'sales' ? 'Factura' : 'Código de Pedido'}
            </Table.HeaderCell>
            {this.state.type === 'sales' && (
              <Table.HeaderCell textAlign='center'>Tipo</Table.HeaderCell>
            )}
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {loadingWeeklyAnalysis && (
            <Table.Row>
              <Table.Cell colSpan='100%'>
                <Loader active inline='centered' />
              </Table.Cell>
            </Table.Row>
          )}
          {!loadingWeeklyAnalysis && this.renderWeeklyData(weeklyAnalysis)}
        </Table.Body>
      </Table>
    );
  }

  renderBarCells(data, barType) {
    const {type, activeIndex} = this.state;
    const isCurrentType = type === barType;
    const {base, selected} = barColors[barType];

    return data.map((entry, index) => {
      return (
        <Cell
          key={`cell-${index}`}
          cursor='pointer'
          fill={isCurrentType && index === activeIndex ? selected : base}
        />
      );
    });
  }

  renderBar(data, barType) {
    if (!this.props[barType]) {
      return null;
    }

    return (
      <Bar
        dataKey={barType}
        fill={barColors[barType].base}
        onClick={this.handleClick}
        label={{'position': 'top'}}
      >
        {this.renderBarCells(data, barType)}
      </Bar>
    );
  }

  render() {
    const {data, legendPosition} = this.props;

    if (!data || data.length === 0) {
      return null;
    }

    return (
      <div className='chart'>
        <ResponsiveContainer width='100%' height={350}>
          <BarChart
            data={data}
            margin={{'top': 30, 'right': 50, 'left': 0, 'bottom': 0}}
          >
            <XAxis
              dataKey='week'
              tickFormatter={this.renderXLabels.bind(this)}
            />
            <YAxis />
            <CartesianGrid stroke={colors.menuWhite} vertical={false} />
            <Tooltip content={this.renderTooltip} cursor={{'fillOpacity': 0}} />
            <Legend
              verticalAlign={legendPosition}
              align='left'
              content={this.renderLegend.bind(this)}
            />
            {this.renderBar(data, 'sales')}
            {this.renderBar(data, 'lostSales')}
            {this.renderBar(data, 'orders')}
          </BarChart>
        </ResponsiveContainer>
        {this.renderTable()}
      </div>
    );
  }
}

SalesAndOrdersChart.propTypes = {
  'data': PropTypes.arrayOf(PropTypes.object).isRequired,
};

SalesAndOrdersChart.defaultProps = {
  'sales': true,
  'legendPosition': 'top',
};

export default SalesAndOrdersChart;
